import { useMemo } from "react";
import { Box, Heading, Tooltip, Icon, HStack } from "@chakra-ui/react";
import { Info } from "lucide-react";
import { useSelector } from "react-redux";
import _ from "lodash";

import FilterForm from "../../components/FilterForm";
import Loader from "../../components/loader";
import { getReportDescription, getReportRangeString } from "../../store/helpers";
import ArrivalRatesPerDayReport from "../../components/branches/reports/ArrivalRatesPerDay";
import { REPORT_RANGES, REPORT_TYPE_IDS } from "../../store/constants";
import { Helmet } from "react-helmet";
import DownloadReport from "../../components/DownloadReport";
import SelectBranchInfo from "../../components/misc/SelectBranchInfo";
import ReportPresentationControls from "../../components/reports/ReportPresentationControls";
import ReportPresentationSummary from "../../components/reports/ReportPresentationSummary";
import { ARRIVAL_RATES_SUMMARY_COLUMN_DEFINITIONS } from "../../store/renderConfig";
import { useReportPresentation } from "../../hooks/useReportPresentation";
import { downloadArrivalRatesSummaryCsvFiles } from "../../store/arrivalRatesSummaryExport";

const ArrivalRatesPerDay = ({ showFilterForm = true, showTitle = 1 }) => {
  const { loading, reportRange, reportsData, branchId } = useSelector(
    (state) => state.defaultReducer
  );
  const reportTypeId =
    reportRange === REPORT_RANGES.WEEK
      ? REPORT_TYPE_IDS.weekly_branch_arrival_rates_per_day
      : REPORT_TYPE_IDS.monthly_branch_arrival_rates_per_day;
  const data = useMemo(() => {
    let reports = reportsData.filter((item) => item.reportTypeId === reportTypeId);
    if (branchId.length > 0) reports = reports.filter((item) => item.branchId === branchId);
    return reports.map((item) => item.data) || [];
  }, [branchId, reportTypeId, reportsData]);
  const {
    effectiveRenderConfig,
    isSaving,
    presentationSummaryItems,
    saveRenderConfig,
    setRenderConfig,
  } = useReportPresentation({
    reportTypeId,
    columnDefinitions: ARRIVAL_RATES_SUMMARY_COLUMN_DEFINITIONS,
  });
  const csvScopes = useMemo(
    () => data.map((item) => ({ ...item, scopeName: item.branchName })),
    [data]
  );

  return (
    <Box>
      {showTitle === 1 && (
        <Helmet>
          <title>{getReportRangeString(reportRange)} Branch Arrival Rates Per Day</title>
        </Helmet>
      )}
      {showFilterForm ? <FilterForm /> : null}
      {loading ? <Loader /> : (
        <>
          {!_.isEmpty(data) ? (
            <>
              <HStack flexDirection="row" justifyContent="space-between">
                <Heading fontSize="34px" color="#222" fontWeight="700">
                  Branch Arrival Rates Per Day
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <Icon as={Info} w={5} h={5} ml={2} cursor="pointer" />
                    </Tooltip.Trigger>
                    <Tooltip.Positioner>
                      <Tooltip.Content>
                        {getReportDescription(reportRange).BranchesArrivalRatesPerDay}
                      </Tooltip.Content>
                    </Tooltip.Positioner>
                  </Tooltip.Root>
                </Heading>
                <DownloadReport
                  reportRange={reportRange}
                  reportTypeId={reportTypeId}
                  renderConfig={effectiveRenderConfig}
                  onSave={saveRenderConfig}
                  isSaving={isSaving}
                  onDownloadCsv={() =>
                    downloadArrivalRatesSummaryCsvFiles({
                      scopes: csvScopes,
                      renderConfig: effectiveRenderConfig,
                      filenamePrefix: "branch_arrival_rates_per_day",
                    })
                  }
                />
              </HStack>
              <ReportPresentationControls
                renderConfig={effectiveRenderConfig}
                onChange={setRenderConfig}
                columnDefinitions={ARRIVAL_RATES_SUMMARY_COLUMN_DEFINITIONS}
                defaultSortColumn="total"
              />
              <ReportPresentationSummary items={presentationSummaryItems} />
              <ArrivalRatesPerDayReport
                data={data}
                renderConfig={effectiveRenderConfig}
                onRenderConfigChange={setRenderConfig}
              />
            </>
          ) : (
            <SelectBranchInfo message="Update Filters and Hit Search to retrieve data" />
          )}
        </>
      )}
    </Box>
  );
};

export default ArrivalRatesPerDay;
